import {
  CircularProgress,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  makeStyles,
  TextField,
  TextFieldProps,
  Typography,
} from '@material-ui/core'
import { Close, ExpandMore } from '@material-ui/icons'
import SearchIcon from '@material-ui/icons/Search'
import classnames from 'classnames'
import get from 'lodash/get'
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  createRef,
} from 'react'

import { usePrevious } from '../../utils/hooks'

const GLOBAL_HOTKEY_KEYCODE = '/'

const useStyles = makeStyles((theme) => ({
  resultsListWrapper: { position: 'relative' },
  resultsList: {
    position: 'absolute',
    backgroundColor: '#FFF',
    zIndex: 2000,
    border: `1px solid ${theme.palette.grey[300]}`,
    borderTop: 'none',
    width: 'auto',
    [theme.breakpoints.only('xs')]: {
      maxWidth: '90vw',
    },
    maxWidth: 'max(40vw, 100%)',
    maxHeight: '50vh',
    overflowY: 'scroll',
    overflowX: 'hidden',
    boxShadow: `0 0px 2px ${theme.palette.grey[600]}`,
  },
  resultsItem: {
    maxWidth: '100%',
    '&:not(:last-of-type)': {
      borderBottom: `1px solid ${theme.palette.grey[400]}`,
    },
    '&.selected': {
      backgroundColor: theme.palette.primary.light,
      '& .MuiTypography-root': {
        fontWeight: 700,
      },
    },
  },
  resultsItemText: {
    maxWidth: '100%',
    '& .MuiTypography-root': {
      maxWidth: '100%',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      // If we have a text-only search result, we can pre-space
      // it so bold text doesn't cause layout shift
      '&.bold-pre-spacedx': {
        display: 'inline-flex',
        alignItems: 'center',
        flexDirection: 'column',
        justifyContent: 'space-between',
        '&::after': {
          content: 'attr(data-text)',
          height: 0,
          visibility: 'hidden',
          overflow: 'hidden',
          userSelect: 'none',
          pointerEvents: 'none',
          fontWeight: 700,
          '@media speech': {
            display: 'none',
          },
        },
      },
    },
  },
  inputAdornmentEnd: {
    width: 54,
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  inputAdornmentStart: {},
}))

export type SearchItem = unknown

export type SearchProps<T extends SearchItem> = {
  onSearchChange: (value: string) => void
  search: string
  results: T[] | undefined
  list?: T[]
  onSelectItem: (item: T) => void
  onClear?: () => void
  onBlur?: () => void
  formatResults: (item: T) => React.ReactNode
  keyBy: string
  loading?: boolean
  maxResults?: number
  useGlobalHotkey?: boolean
  dropdown?: boolean
  /** ID of list item to select on load/blur, as implied by `keyBy`. Requires `list` to be set */
  defaultValue?: string
} & Omit<TextFieldProps, 'results' | 'defaultValue'>

export function SearchBase<T extends SearchItem>({
  onSearchChange,
  results,
  search,
  list,
  onSelectItem,
  onBlur,
  onFocus,
  formatResults,
  keyBy,
  defaultValue,
  maxResults = 5,
  loading = false,
  useGlobalHotkey = false,
  dropdown = false,
  ...textFieldProps
}: SearchProps<T>): JSX.Element {
  const classes = useStyles()
  const [selectedItem, setSelectedItem] = useState<T>()
  const [showDropdown, setShowDropdown] = useState(false)
  const [selectedResultIndex, setSelectedResultIndex] = useState(-1)
  const [isFocused, setIsFocused] = useState(false)
  const prevIsFocused = usePrevious(isFocused)
  const inputRef = useRef<HTMLInputElement>(null)
  const [resultListRefs, setResultListRefs] = useState<
    React.RefObject<HTMLDivElement>[]
  >([])

  const autoFocus =
    /* list?.length && defaultValue ? false : */ textFieldProps.autoFocus

  const resultsList =
    showDropdown && !isFocused && list?.length
      ? list
      : results?.length && isFocused
      ? results.slice(0, maxResults)
      : []

  function handleSearchChange(event: React.ChangeEvent<HTMLInputElement>) {
    setSelectedResultIndex(-1)
    onSearchChange(event.target.value)
  }

  function handleSelect(item: T) {
    setSelectedItem(item)
    setShowDropdown(false)
    onSelectItem(item)
    if (results) {
      setSelectedResultIndex(
        results.findIndex((result) => get(result, keyBy) === get(item, keyBy)),
      )
    } else {
      setSelectedResultIndex(-1)
    }
    inputRef.current?.blur()
  }

  function handleShowDropdown(show: boolean) {
    setShowDropdown(show)
    if (show && selectedItem && list?.length) {
      setSelectedResultIndex(
        list.findIndex((result) => {
          return get(result, keyBy) === get(selectedItem, keyBy)
        }),
      )
    }
  }

  function handleKeyPress(event: React.KeyboardEvent<HTMLInputElement>) {
    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault()
        if (!resultsList.length) return
        setSelectedResultIndex((index) =>
          index > 0 ? index - 1 : resultsList.length - 1,
        )
        break
      case 'ArrowDown':
        event.preventDefault()
        if (!resultsList.length) return
        setSelectedResultIndex((index) =>
          index < resultsList.length - 1 ? index + 1 : 0,
        )
        break
      case 'Enter':
        event.preventDefault()
        if (!resultsList.length) return
        if (resultsList.length && selectedResultIndex > -1)
          handleSelect(resultsList[selectedResultIndex])
        break
      default:
        if (/^[a-zA-Z]$/.test(event.key) && showDropdown) {
          handleClear()
        }
    }
  }

  function handleFocus(
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) {
    setIsFocused(true)
    setTimeout(() => setSelectedItem(undefined), 50)
    if (typeof onFocus === 'function') onFocus(e)
  }

  function handleBlur() {
    setTimeout(() => setIsFocused(false), 100)
  }

  function handleClear() {
    setFocused()
    setSelectedItem(undefined)
    onSearchChange('')
    setShowDropdown(false)
  }

  function setFocused() {
    inputRef.current?.focus()
    setTimeout(() => setIsFocused(true), 100)
  }

  /** If we get a new result set, select the first item */
  useEffect(() => {
    if (results?.length) {
      setSelectedResultIndex(0)
    }
  }, [results?.length])

  /** If we have a defaultValue, and the component is blurred, select that item from the list */
  useEffect(() => {
    if (list?.length && defaultValue && !isFocused && !selectedItem) {
      const newSelectedItem = list.find((item) => {
        const key = get(item, keyBy)
        return key === defaultValue
      })
      if (newSelectedItem) {
        setSelectedItem(newSelectedItem)
      }
    }
  }, [defaultValue, isFocused, keyBy, list, list?.length, selectedItem])

  /** Keep an up-to-date list of refs for the results list */
  useEffect(() => {
    // add or remove refs
    setResultListRefs((refs) =>
      Array(resultsList.length)
        .fill(undefined)
        .map((_, i) => refs[i] || createRef()),
    )
  }, [resultsList.length])

  /** Scroll to selected item */
  useEffect(() => {
    if (resultListRefs.length && selectedResultIndex > -1 && showDropdown) {
      resultListRefs[selectedResultIndex]?.current?.scrollIntoView({
        behavior: 'auto',
        block: 'start',
      })
    }
  }, [resultListRefs, selectedResultIndex, showDropdown])

  /**
   * Event listener for keypress events, to check if global hotkey has been
   * pressed
   */
  const handleGlobalHotkey = useCallback(function (
    this: Document,
    event: KeyboardEvent,
  ) {
    /**
     * Check that the currently-focused element isn't a text input element
     * (otherwise we can't type the hotkey into any text fields, including the
     * search box itself)
     */
    const disallowedElement =
      event.target instanceof Element &&
      ['INPUT', 'TEXTAREA'].includes(event.target.nodeName)
    if (event.key === GLOBAL_HOTKEY_KEYCODE && !disallowedElement) {
      setFocused()
      event.preventDefault()
    }
  },
  [])

  /** Create global hotkey if required */
  useEffect(() => {
    if (typeof document === 'undefined' || !useGlobalHotkey) return
    document.addEventListener('keypress', handleGlobalHotkey)
    return () => {
      document.removeEventListener('keypress', handleGlobalHotkey)
    }
  }, [handleGlobalHotkey, useGlobalHotkey])

  /** Select the first item if we have results */
  useEffect(() => {
    if (results?.length && selectedResultIndex === -1) {
      setSelectedResultIndex(0)
    }
  }, [results?.length, selectedResultIndex])

  /** Run onBlur after isFocused switches, to allow updates made beforehand to propagate */
  useEffect(() => {
    if (!isFocused && prevIsFocused && typeof onBlur === 'function') {
      onBlur()
    }
  }, [isFocused, onBlur, prevIsFocused])

  return (
    <>
      <TextField
        value={!selectedItem ? search : ''}
        onChange={handleSearchChange}
        onKeyDown={handleKeyPress}
        InputProps={{
          startAdornment: selectedItem ? (
            <InputAdornment
              position="start"
              className={classes.inputAdornmentStart}
            >
              {formatResults(selectedItem)}
            </InputAdornment>
          ) : undefined,
          endAdornment: (
            <InputAdornment
              position="end"
              className={classes.inputAdornmentEnd}
            >
              {loading ? (
                <CircularProgress size={20} />
              ) : (
                (search || selectedItem) && (
                  <IconButton size="small" onClick={handleClear}>
                    <Close />
                  </IconButton>
                )
              )}
              {dropdown ? (
                <IconButton
                  size="small"
                  onClick={() => handleShowDropdown(!showDropdown)}
                >
                  <ExpandMore />
                </IconButton>
              ) : (
                <SearchIcon />
              )}
            </InputAdornment>
          ),
        }}
        inputRef={inputRef}
        onFocus={handleFocus}
        onBlur={handleBlur}
        {...textFieldProps}
        placeholder={
          !selectedItem ? textFieldProps.placeholder || 'Search...' : undefined
        }
        autoFocus={autoFocus}
      />
      {!!resultsList.length && (
        <div className={classes.resultsListWrapper}>
          <List className={classes.resultsList} disablePadding>
            {resultsList.map((result, index) => {
              const formattedResult = formatResults(result)
              const key = get(result, keyBy)
              if (typeof key !== 'string')
                throw new Error(
                  `keyBy must refer to a property in result that is of type string (keyBy is ${keyBy})`,
                )
              return (
                <ListItem
                  key={key}
                  className={classnames(classes.resultsItem, {
                    selected: index === selectedResultIndex,
                  })}
                  ref={resultListRefs[index]}
                  button
                  onClick={() => handleSelect(result)}
                >
                  <ListItemText
                    className={classes.resultsItemText}
                    disableTypography={true}
                  >
                    <Typography
                      component="div"
                      data-text={
                        typeof formattedResult === 'string'
                          ? formattedResult
                          : undefined
                      }
                      className={classnames({
                        'bold-pre-spaced': typeof formattedResult === 'string',
                      })}
                    >
                      {formattedResult}
                    </Typography>
                  </ListItemText>
                </ListItem>
              )
            })}
          </List>
        </div>
      )}
    </>
  )
}

export default SearchBase
